import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import { useCallback, useEffect, useRef, useState } from 'react'

import Message from './Message'


import {
    getDisplaySpeakerData,
    shouldShowTranscriptSpeaker,
} from './speakerHelper'

import { getSectionTitle } from './sectionHelper';
import { loadTrack } from './audioHelper';
import ContinueListeningButton from './ContinueListeningButton';
import { previewContinuationSeconds } from '../utils/episodePlaybackLabel';

const Transcript = ({
    // From redux.
    authenticated,

    // Passed in.
    autoscrollPaddingBottom,
    curTime,
    currentSection,
    previewSpeakerId,
    isCurrentViewport,
    onContinueListeningClick,
    onManualScroll,
    openSpeakerInfo,
    playing,
    replayRequest = 0,
    scrollToCurrentMessage,
    setCurrentSpeaker,
    setScrollToCurrentMessage,
    transcriptContainerRef,
    track,
    onSignupRequired,
}) => {

    const [message, setMessage] = useState({});
    const autoScrollInProgress = useRef(null);
    const scrollStartTimeout = useRef(null);
    const scrollTimeout = useRef(null);
    const handledReplayRequest = useRef(replayRequest);
    const lastScrolledMessageId = useRef(null);
    const [scrollCompletion, setScrollCompletion] = useState(0);

    const audio = document.getElementById('audio');

    const cancelPendingAutoScroll = useCallback(() => {
        clearTimeout(scrollStartTimeout.current);
        clearTimeout(scrollTimeout.current);
        scrollStartTimeout.current = null;
        scrollTimeout.current = null;
        autoScrollInProgress.current = false;
    }, []);

    const finishAutoScroll = useCallback(() => {
        autoScrollInProgress.current = false;
        // Recheck the media clock even if a paused seek produces no more timeupdates.
        setScrollCompletion(completion => completion + 1);
    }, []);

    const currentMessageAtTime = useCallback(time => {
        let start = 0;
        let end = track.transcript.length - 1;
        while (start <= end) {
            const middle = Math.floor((start + end) / 2);
            if (track.transcript[middle].timeSeconds <= time) start = middle + 1;
            else end = middle - 1;
        }
        return track.transcript[Math.max(0, end)];
    }, [track.transcript]);

    useEffect(() => {
        const transcriptContainer = transcriptContainerRef.current;
        if (!isCurrentViewport || !audio || !transcriptContainer) {
            return undefined;
        }

        const pauseForManualScroll = () => {
            if (audio.src !== track.mp3URL) return;
            cancelPendingAutoScroll();
            onManualScroll?.();
            if (!audio.paused) audio.pause();
            setScrollToCurrentMessage(false);
        };
        const onWheel = event => {
            if (!event.ctrlKey && Math.abs(event.deltaY) > Math.abs(event.deltaX)) pauseForManualScroll();
        };
        let touchPosition;
        const onTouchStart = event => {
            touchPosition = event.touches.length === 1 ? event.touches[0] : null;
        };
        const onTouchMove = event => {
            if (!touchPosition || event.touches.length !== 1) return;
            const touch = event.touches[0];
            if (Math.abs(touch.clientY - touchPosition.clientY) > Math.abs(touch.clientX - touchPosition.clientX)) {
                pauseForManualScroll();
            }
        };
        const onKeyDown = event => {
            if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key) &&
                !event.target.closest('button, input, textarea, select, a, [contenteditable="true"]')) {
                pauseForManualScroll();
            }
        };
        const onScroll = () => {
            if (audio.src !== track.mp3URL) {
                return;
            }
            clearTimeout(scrollTimeout.current);
            // Keep programmatic scrolling from disabling transcript sync.
            if (autoScrollInProgress.current) {
                scrollTimeout.current = setTimeout(finishAutoScroll, 300);
            } else {
                pauseForManualScroll();
            }
        }

        transcriptContainer.addEventListener('scroll', onScroll);
        // User gestures take precedence even while a smooth follow scroll is pending.
        transcriptContainer.addEventListener('wheel', onWheel, { passive: true });
        transcriptContainer.addEventListener('touchstart', onTouchStart, { passive: true });
        transcriptContainer.addEventListener('touchmove', onTouchMove, { passive: true });
        transcriptContainer.addEventListener('keydown', onKeyDown);

        return () => {
            cancelPendingAutoScroll();
            transcriptContainer.removeEventListener('scroll', onScroll);
            transcriptContainer.removeEventListener('wheel', onWheel);
            transcriptContainer.removeEventListener('touchstart', onTouchStart);
            transcriptContainer.removeEventListener('touchmove', onTouchMove);
            transcriptContainer.removeEventListener('keydown', onKeyDown);
        }
    }, [audio, isCurrentViewport, onManualScroll, setScrollToCurrentMessage, track.mp3URL, transcriptContainerRef, cancelPendingAutoScroll, finishAutoScroll]);

    const scrollTranscript = useCallback((elementId, scrollToTop = false) => {
        if (!isCurrentViewport || !audio || audio.src !== track.mp3URL) {
            return;
        }
        const transcriptContainer = transcriptContainerRef.current;
        if (!transcriptContainer) return;
        // A preview and its episode transcript can share message IDs. Resolve
        // the passage within this transcript, never another player's copy.
        const scrollTarget = scrollToTop ? transcriptContainer : Array.from(
            transcriptContainer.querySelectorAll('[id]'),
        ).find(element => element.id === elementId);
        if (!scrollTarget) return;

        cancelPendingAutoScroll();
        autoScrollInProgress.current = true;
        // Let layout settle before scrolling newly rendered transcript content.
        scrollStartTimeout.current = setTimeout(() => {
            scrollStartTimeout.current = null;
            if (audio.src !== track.mp3URL || transcriptContainerRef.current !== transcriptContainer ||
                !transcriptContainer.contains(scrollTarget)) {
                cancelPendingAutoScroll();
                return;
            }
            if (scrollToTop) {
                transcriptContainer.scrollTo({ top: 0, behavior: 'smooth' });
            } else {
                const containerRect = transcriptContainer.getBoundingClientRect();
                const targetRect = scrollTarget.getBoundingClientRect();
                const viewportBottom = containerRect.top + transcriptContainer.clientTop + transcriptContainer.clientHeight;
                // The target's box already includes the bottom follow buffer.
                // Scroll only the transcript so outer app frames stay in place.
                const desiredTop = transcriptContainer.scrollTop + targetRect.bottom - viewportBottom;
                const maxScrollTop = Math.max(0, transcriptContainer.scrollHeight - transcriptContainer.clientHeight);
                transcriptContainer.scrollTo({ top: Math.max(0, Math.min(desiredTop, maxScrollTop)), behavior: 'smooth' });
            }
            lastScrolledMessageId.current = elementId;
        }, 50);

        scrollTimeout.current = setTimeout(finishAutoScroll, 1000);
    }, [isCurrentViewport, audio, track.mp3URL, transcriptContainerRef, cancelPendingAutoScroll, finishAutoScroll]);

    useEffect(() => {
        if (!replayRequest || handledReplayRequest.current === replayRequest) return;
        handledReplayRequest.current = replayRequest;
        cancelPendingAutoScroll();
        if (!isCurrentViewport || !audio || audio.src !== track.mp3URL) return;
        const firstMessage = track.transcript[0];
        if (!firstMessage) return;
        setMessage(firstMessage);
        // The sticky preview header shares this container, so return the
        // container to zero rather than placing the first message beneath it.
        scrollTranscript(firstMessage.id, true);
    }, [replayRequest, isCurrentViewport, audio, track, scrollTranscript, cancelPendingAutoScroll]);

    // Media progress and Follow audio share the same current-passage lookup.
    useEffect(() => {
        if (!scrollToCurrentMessage) {
            cancelPendingAutoScroll();
            lastScrolledMessageId.current = null;
        }
        if (!isCurrentViewport || audio?.src !== track.mp3URL) return;
        const currentMessage = currentMessageAtTime(audio.currentTime);
        if (!currentMessage) return;
        setMessage(currentMessage);
        if (scrollToCurrentMessage && !autoScrollInProgress.current &&
            currentMessage.id !== lastScrolledMessageId.current) {
            scrollTranscript(currentMessage.id);
        }
    }, [audio, curTime, isCurrentViewport, track.mp3URL, currentMessageAtTime,
        scrollToCurrentMessage, scrollCompletion, cancelPendingAutoScroll, scrollTranscript]);

    useEffect(() => {
        if (message && Object.keys(message).length !== 0) {
            setCurrentSpeaker(getDisplaySpeakerData(message.speakerId, track.speakers, message.anonymousDisplay));
        } else {
            setCurrentSpeaker(null);
        }
    }, [message])

    const seekFn = (timeSeconds) => () => {
        setMessage({});
        if (audio.src !== track.mp3URL) {
            loadTrack(track.mp3URL, timeSeconds)
            audio.play().catch(function () {
                // Browser doesn't allow play until user click.
            });
        } else {
            seek(timeSeconds);
        }
    }

    const seek = (timeSeconds) => {
        setScrollToCurrentMessage(true);
        audio.play().catch(function () { });
        audio.currentTime = timeSeconds;
    }

    const messagesForTrack = (track, start, numMessages) => {
        const messages = [];
        for (let i = start; i < start + numMessages; i++) {
            const message = track.transcript[i];
            messages.push(message);
        }
        return messages;
    }

    return (
        <div style={{ width: '100%' }}>
            <div>
                <div style={{ paddingBottom: '20px', paddingTop: previewSpeakerId ? '0px' : 'var(--transcript-header-height, 44px)' }}>
                    {track.sectionInfos.map((section, sectionIndex) => (
                        <div key={sectionIndex}>
                            {track.sectionInfos.length < 2 ? <></> :
                                <div style={{
                                    opacity: !scrollToCurrentMessage || currentSection === sectionIndex || !playing ? 1.0 : 0.55,
                                }}>
                                    <div className='section'>
                                        <button style={sectionIndex === 0 ? {} : { borderTop: '1px solid var(--app-separator)' }} className='sectionButtonInTranscript' onClick={seekFn(section.startTime)}>
                                            <div className='heading'>
                                                {getSectionTitle(section)}
                                            </div>
                                        </button>
                                    </div>
                                </div>
                            }
                            {
                                messagesForTrack(track, section.startIndex, section.numMessages).map((m, index, sectionMessages) => (
                                    <div key={m.id}>
                                        <div id={m.id} style={{
                                            // add padding/margin so autoscroll doesn't scroll all the
                                            // way to the bottom and leaves some buffer.
                                            paddingBottom: `${autoscrollPaddingBottom}px`,
                                            marginBottom: `-${autoscrollPaddingBottom}px`,
                                        }}>
                                            {playing && m.id === message.id && curTime > 0 ?
                                                <Message
                                                    authenticated={authenticated}
                                                    message={m}
                                                    isCurrent={true}
                                                    curTime={curTime}
                                                    openSpeakerInfo={openSpeakerInfo}
                                                    playing={playing}
                                                    scrollToCurrentMessage={scrollToCurrentMessage}
                                                    seekFn={seekFn}
                                                    speaker={getDisplaySpeakerData(m.speakerId, track.speakers, m.anonymousDisplay)}
                                                    showSpeakerDetails={shouldShowTranscriptSpeaker(index, sectionMessages)}
                                                    onSignupRequired={onSignupRequired}
                                                />
                                                :
                                                // Same as above but don't refer to curTime to allow for faster updates.
                                                <Message
                                                    authenticated={authenticated}
                                                    message={m}
                                                    isCurrent={false}
                                                    openSpeakerInfo={openSpeakerInfo}
                                                    playing={playing}
                                                    scrollToCurrentMessage={scrollToCurrentMessage}
                                                    seekFn={seekFn}
                                                    speaker={getDisplaySpeakerData(m.speakerId, track.speakers, m.anonymousDisplay)}
                                                    showSpeakerDetails={shouldShowTranscriptSpeaker(index, sectionMessages)}
                                                    onSignupRequired={onSignupRequired}
                                                />
                                            }

                                            {onContinueListeningClick && index === section.numMessages - 1 &&
                                                <div style={{ opacity: !playing || (m.id === message.id && curTime > 0) ? 1 : 0.5, textAlign: 'center' }}>
                                                    <ContinueListeningButton continueListening={onContinueListeningClick}
                                                        totalSeconds={track.fullTrackTotalTimeSecs}
                                                        startSeconds={previewContinuationSeconds(track.previewStartTime,
                                                            audio?.src === track.mp3URL ? audio.currentTime : curTime,
                                                            audio?.src === track.mp3URL && Number.isFinite(audio.duration) && audio.duration > 0
                                                                ? audio.duration : track.totalTimeSeconds)} />
                                                </div>
                                            }
                                        </div>
                                        
                                    </div>
                                ))
                            }
                        </div>
                    ))}
                </div>

            </div>
        </div >
    );
};

Transcript.propTypes = {
    openSpeakerInfo: PropTypes.func.isRequired,
}

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
})

const mapActionsToProps = {

}

export default connect(mapStateToProps, mapActionsToProps)(Transcript);
